import { useEffect } from "react";

import { actions as layoutActions } from "reducers/layout";

import * as config from "util/config";

/**
 * Id de configuración (`configuration.listConfiguration`): única fuente literal del flag Mis cuentas v3.
 *
 * @see {@link isUxMyAccountsImprovementsEnabled}
 */
export const UX_IMPROVEMENTS_MY_ACCOUNTS_FUNCTIONALITIES_ID = "frontend.UXImprovements.MyAccounts.functionalities";

/**
 * Id de configuración: UX Transferencias v3 (histórico, filtros ui-kit, confirmación, ticket).
 *
 * @see {@link isUxTransfersImprovementsEnabled}
 */
export const UX_IMPROVEMENTS_TRANSFERS_FUNCTIONALITIES_ID = "frontend.UXImprovements.Transfers.functionalities";

/**
 * Id de configuración: cuentas propias externas + límites CVU en transferencia interna.
 *
 * @see {@link isUxTransfersOwnImprovementsEnabled}
 */
export const UX_IMPROVEMENTS_TRANSFERS_OWN_FUNCTIONALITIES_ID = "frontend.UXImprovements.Transfers.own.functionalities";

export function isUxDashboardImprovementsEnabled() {
    return config.getBoolean("frontend.UXImprovements.Dashboard.functionalities", false);
}

/**
 * UX "Mis cuentas" v3 (`/v3/accounts` vs `/v2/accounts`). Default OFF: se usa lista v2.
 * Requiere además {@link isUxDashboardImprovementsEnabled} para servir `/v3/accounts` (shell privado).
 *
 * @returns {boolean}
 */
export function isUxMyAccountsImprovementsEnabled() {
    return config.getBoolean(UX_IMPROVEMENTS_MY_ACCOUNTS_FUNCTIONALITIES_ID, false);
}

/**
 * UX Transferencias v3 (`frontend.UXImprovements.Transfers.functionalities`). Default OFF.
 * Confirmación/ticket V3, histórico v3, labels.
 *
 * @returns {boolean}
 */
export function isUxTransfersImprovementsEnabled() {
    return config.getBoolean(UX_IMPROVEMENTS_TRANSFERS_FUNCTIONALITIES_ID, false);
}

/**
 * Cuentas propias de otros bancos + CVU en internas.
 * (`frontend.UXImprovements.Transfers.own.functionalities`). Default OFF.
 *
 * @returns {boolean}
 */
export function isUxTransfersOwnImprovementsEnabled() {
    return config.getBoolean(UX_IMPROVEMENTS_TRANSFERS_OWN_FUNCTIONALITIES_ID, false);
}

/**
 * UX Transferencias v3: requiere shell privado (dashboard UX). No compatible con DefaultLayout legacy.
 *
 * @returns {boolean}
 */
export function isTransferHistoricV3Enabled() {
    return isUxTransfersImprovementsEnabled() && isUxDashboardImprovementsEnabled();
}

/**
 * Id de configuración (`configuration.listConfiguration`): única fuente literal del flag Credin v3.
 *
 * @see {@link isUxCredinImprovementsEnabled}
 */
export const UX_IMPROVEMENTS_CREDIN_FUNCTIONALITIES_ID = "frontend.UXImprovements.Credin.functionalities";

/**
 * UX Credin v3 (`frontend.UXImprovements.Credin.functionalities`).
 *
 * Provisional: default `true` mientras se despliega la experiencia Credin v3; al finalizar el rollout
 * volver el default a `false` para que la ausencia del flag deshabilite la v3.
 *
 * @returns {boolean}
 */
export function isUxCredinImprovementsEnabled() {
    return config.getBoolean(UX_IMPROVEMENTS_CREDIN_FUNCTIONALITIES_ID, false);
}

/**
 * Id de configuración: Metrocorp v3 + tenencia ESCO.
 *
 * @see {@link isUxMetrocorpImprovementsEnabled}
 */
export const UX_IMPROVEMENTS_METROCORP_FUNCTIONALITIES_ID = "frontend.UXImprovements.Metrocorp.functionalities";

/**
 * UX Metrocorp v3 (`frontend.UXImprovements.Metrocorp.functionalities`). Default OFF: SOAP legacy.
 *
 * @returns {boolean}
 */
export function isUxMetrocorpImprovementsEnabled() {
    return config.getBoolean(UX_IMPROVEMENTS_METROCORP_FUNCTIONALITIES_ID, false);
}

/**
 * Id de configuración: pestaña Movimientos de Metrocorp v3.
 *
 * @see {@link isUxMetrocorpMovementsEnabled}
 */
export const UX_IMPROVEMENTS_METROCORP_MOVEMENTS_FUNCTIONALITIES_ID =
    "frontend.UXImprovements.Metrocorp.movements.functionalities";

/**
 * Pestaña Movimientos de Metrocorp v3. Default OFF.
 *
 * @returns {boolean}
 */
export function isUxMetrocorpMovementsEnabled() {
    return config.getBoolean(UX_IMPROVEMENTS_METROCORP_MOVEMENTS_FUNCTIONALITIES_ID, false);
}

/**
 * Id de configuración: widget Metrocorp en dashboard v3.
 *
 * @see {@link isUxMetrocorpWidgetEnabled}
 */
export const UX_IMPROVEMENTS_METROCORP_WIDGET_FUNCTIONALITIES_ID =
    "frontend.UXImprovements.Metrocorp.widget.functionalities";

/**
 * Widget Metrocorp del dashboard (`frontend.UXImprovements.Metrocorp.widget.functionalities`).
 * Default OFF: se mantiene `CardMetrocorpDashboard` / `widgets.metrocorp`.
 *
 * @returns {boolean}
 */
export function isUxMetrocorpWidgetEnabled() {
    return config.getBoolean(UX_IMPROVEMENTS_METROCORP_WIDGET_FUNCTIONALITIES_ID, false);
}

/**
 * Id de configuración: cards Metrocorp de /v3/accounts con tenenciaval ESCO.
 *
 * @see {@link isUxMetrocorpAccountImprovementsEnabled}
 */
export const UX_IMPROVEMENTS_METROCORP_ACCOUNT_FUNCTIONALITIES_ID =
    "frontend.UXImprovements.Metrocorp.account.functionalities";

/**
 * Cuentas Metrocorp ESCO (`frontend.UXImprovements.Metrocorp.account.functionalities`).
 * Default OFF: `accounts.metrocorp.list` sigue usando SOAP.
 *
 * @returns {boolean}
 */
export function isUxMetrocorpAccountImprovementsEnabled() {
    return config.getBoolean(UX_IMPROVEMENTS_METROCORP_ACCOUNT_FUNCTIONALITIES_ID, false);
}

/**
 * UX Credin v3: requiere shell privado (dashboard UX). No compatible con DefaultLayout legacy.
 *
 * @returns {boolean}
 */
export function isCredinV3Enabled() {
    return isUxCredinImprovementsEnabled() && isUxDashboardImprovementsEnabled();
}

/**
 * Orígenes (vistos en `status.lastHref`) que “devuelven al inicio” en lugar de a la ruta default
 * del módulo. Si el usuario llegó al ticket desde inicio (v3 o legacy) o desde una lista de
 * autorizaciones/historial, esperamos volver allí.
 *
 * @type {string[]}
 */
const V3_SHELL_BACK_DASHBOARD_ORIGINS = ["/v3/dashboard", "/desktop"];

const MASS_ECHEQ_MANAGE_BATCH_DETAIL_PATH = /^\/v2\/echeqs\/mass-management\/[^/]+\/detail$/;

/**
 * @param {string|undefined|null} pathname
 * @returns {boolean}
 */
export function isMassEcheqManageBatchDetailPath(pathname) {
    return typeof pathname === "string" && MASS_ECHEQ_MANAGE_BATCH_DETAIL_PATH.test(pathname);
}

/**
 * Determines the shell back path for a ticket embedded in the v3 shell.
 *
 * Resolution order:
 *  1. `/transactions/list` if user came from the transactions history list.
 *  2. `/pendingTransaction/list` if user came from the pending authorizations list.
 *  3. Mass-management batch detail if the ticket was opened from that screen.
 *  4. Post-login home (`/v3/dashboard` or `/desktop`) when origin is a dashboard.
 *  5. `flowDefaultPath` otherwise (ruta default del módulo, p. ej. `/echeqs`, `/servicePayments`).
 *
 * No depende del flag UX: el `lastHref` siempre se setea, y la decisión de volver a inicio respeta
 * el home post-login (`/v3/dashboard` vs `/desktop`) vía {@link isUxDashboardImprovementsEnabled}.
 *
 * @param {string} flowDefaultPath Default back path for the current ticket flow.
 * @param {string|undefined|null} lastHrefPathname `status.lastHref.pathname` when available.
 * @returns {string} Resolved shell back path.
 */
export function resolveEmbeddedV3GoBackPath(flowDefaultPath, lastHrefPathname) {
    if (lastHrefPathname === "/transactions/list") {
        return "/transactions/list";
    }
    if (lastHrefPathname === "/pendingTransaction/list") {
        return "/pendingTransaction/list";
    }
    if (isMassEcheqManageBatchDetailPath(lastHrefPathname)) {
        return lastHrefPathname;
    }
    if (V3_SHELL_BACK_DASHBOARD_ORIGINS.includes(lastHrefPathname)) {
        return isUxDashboardImprovementsEnabled() ? "/v3/dashboard" : "/desktop";
    }
    return flowDefaultPath;
}

const ECHEQS_EMIT_MASSIVE_BATCH_DETAIL_PATH = /^\/echeqs\/emit\/massive\/[^/]+\/detail$/;

/** Clave en `location.state` para volver a la pantalla desde la que se abrió el detalle de lote masivo. */
export const ECHEQ_BATCH_DETAIL_RETURN_STATE_KEY = "echeqBatchDetailReturnTo";

/**
 * Clave en `location.state` del detalle de lote: `goBackState` que tenía `/transactions/sign` al abrir
 * el detalle desde "Ver listado", para restaurarlo al volver y no perder el botón atrás de la firma.
 */
export const ECHEQ_BATCH_DETAIL_SIGN_GO_BACK_STATE_KEY = "echeqBatchDetailSignGoBack";

/**
 * @param {import("history").Location|undefined|null} location
 * @returns {string|null} pathname de retorno si viene en el state de la ruta
 */
export function getEcheqBatchDetailReturnToFromLocation(location) {
    const raw = location?.state?.[ECHEQ_BATCH_DETAIL_RETURN_STATE_KEY];
    return typeof raw === "string" && raw.startsWith("/") ? raw : null;
}

/**
 * @param {import("history").Location|undefined|null} location
 * @returns {string|null}
 */
export function getEcheqBatchDetailPreservedSignGoBackFromLocation(location) {
    const raw = location?.state?.[ECHEQ_BATCH_DETAIL_SIGN_GO_BACK_STATE_KEY];
    return typeof raw === "string" && raw.startsWith("/") ? raw : null;
}

/**
 * Destino de navegación al detalle de líneas de un lote masivo, guardando el pathname de retorno.
 *
 * @param {string} idTransaction
 * @param {string} returnPathname p. ej. `window.location.pathname` al hacer click
 * @param {string} [signGoBackState] si se abre desde `/transactions/sign`, el `state.goBackState` de esa ruta
 * @returns {{ pathname: string, state?: object }}
 */
export function buildEcheqMassiveBatchDetailLocation(idTransaction, returnPathname, signGoBackState) {
    const pathname = `/echeqs/emit/massive/${idTransaction}/detail`;
    const state = {};
    if (typeof returnPathname === "string" && returnPathname.startsWith("/")) {
        state[ECHEQ_BATCH_DETAIL_RETURN_STATE_KEY] = returnPathname;
    }
    if (typeof signGoBackState === "string" && signGoBackState.startsWith("/")) {
        state[ECHEQ_BATCH_DETAIL_SIGN_GO_BACK_STATE_KEY] = signGoBackState;
    }
    if (Object.keys(state).length === 0) {
        return { pathname };
    }
    return { pathname, state };
}

/**
 * Back target for `/echeqs/emit/massive/:idTransaction/detail` (batch line detail).
 * Reuses {@link resolveEmbeddedV3GoBackPath} for cross-app entry points; when the user
 * navigated from another e-CHEQ route (including `/echeqs` or `/v2/echeqs/*`), returns that pathname.
 *
 * @param {string|undefined|null} lastHrefPathname `status.lastHref.pathname` when available.
 * @returns {string}
 */
export function resolveEcheqsEmitMassiveBatchDetailGoBackPath(lastHrefPathname) {
    if (typeof lastHrefPathname !== "string" || lastHrefPathname.length === 0) {
        return "/echeqs/emit/massive";
    }
    if (ECHEQS_EMIT_MASSIVE_BATCH_DETAIL_PATH.test(lastHrefPathname)) {
        return "/echeqs/emit/massive";
    }
    if (lastHrefPathname.startsWith("/v2/echeqs/mass-management")) {
        return lastHrefPathname.endsWith("/detail") ? "/v2/echeqs/mass-management" : lastHrefPathname;
    }
    if (lastHrefPathname === "/echeqs" || lastHrefPathname.startsWith("/echeqs/")) {
        return lastHrefPathname;
    }
    if (lastHrefPathname.startsWith("/v2/echeqs")) {
        return lastHrefPathname;
    }
    return resolveEmbeddedV3GoBackPath("/echeqs/emit/massive", lastHrefPathname);
}

/**
 * Resolución final del botón atrás del detalle de lote: prioriza el state explícito de la navegación.
 *
 * @param {import("history").Location|undefined|null} location
 * @param {string|undefined|null} lastHrefPathname
 * @returns {string}
 */
export function resolveEcheqMassiveBatchDetailGoBackFromLocationAndLastHref(location, lastHrefPathname) {
    const fromState = getEcheqBatchDetailReturnToFromLocation(location);
    if (fromState) {
        return fromState;
    }
    return resolveEcheqsEmitMassiveBatchDetailGoBackPath(lastHrefPathname);
}

/**
 * Syncs title, back navigation, and mobile header actions to Redux for the v3 shell header.
 * No-ops when the UX dashboard flag is disabled.
 *
 * @param {import("redux").Dispatch} dispatch
 * @param {object} [opts]
 * @param {string} [opts.titleKey] i18n key for the title
 * @param {string} [opts.headerTitle] literal title (e.g. account detail); clears `titleKey` in the reducer
 * @param {string} [opts.headerSubtitle] subtitle below the title (standard shell)
 * @param {import("react").ReactNode|null} [opts.headerSubtitleElement] custom subtitle content
 * @param {string|null} [opts.goBackPath]
 * @param {import("react").ReactNode|null} [opts.rightContentMobile]
 * @param {string[]} [opts.scopes] layout scopes for toast / layout sync
 */
export function dispatchV3ShellLayout(dispatch, opts = {}) {
    if (!isUxDashboardImprovementsEnabled()) {
        return;
    }
    const {
        titleKey,
        headerTitle,
        headerSubtitle,
        headerSubtitleElement,
        goBackPath,
        rightContentMobile,
        scopes,
    } = opts;
    if (titleKey !== undefined) {
        dispatch(layoutActions.setTitleKey(titleKey));
    }
    if (headerTitle !== undefined) {
        dispatch(layoutActions.setHeaderTitle(headerTitle));
    }
    if (headerSubtitle !== undefined) {
        dispatch(layoutActions.setHeaderSubtitle(headerSubtitle));
    }
    if (headerSubtitleElement !== undefined) {
        dispatch(layoutActions.setHeaderSubtitleElement(headerSubtitleElement));
    }
    if (goBackPath !== undefined) {
        dispatch(layoutActions.setGoBackPath(goBackPath));
    }
    if (rightContentMobile !== undefined) {
        dispatch(layoutActions.setRightContentMobile(rightContentMobile));
    }
    if (scopes !== undefined) {
        dispatch(layoutActions.setScopes(scopes));
    }
}

/**
 * Siguiente pantalla bajo `/echeqs` (shell v3): otra ruta volverá a aplicar header embebido.
 * Evita limpiar Redux en el unmount de la pantalla anterior cuando el orden de reconciliación
 * hace que el hijo monte y dispatche `apply` antes de que el padre anterior ejecute `clear`.
 *
 * @param {string} [nextPathname] `history.location.pathname` al desmontar (ya suele ser la ruta destino).
 * @returns {boolean}
 */
export function shouldHandOffEmbeddedLayoutToNextEcheqsRoute(nextPathname) {
    return (
        typeof nextPathname === "string" &&
        (nextPathname.startsWith("/echeqs") || nextPathname.startsWith("/v2/echeqs"))
    );
}

/**
 * Handoff entre pantallas CREDIN v3/legacy embebidas: evita limpiar scopes al ir de confirmación → alta o ticket.
 *
 * @param {string} [nextPathname]
 * @returns {boolean}
 */
export function shouldHandOffEmbeddedLayoutToNextCredinRoute(nextPathname) {
    if (typeof nextPathname !== "string") {
        return false;
    }
    return (
        nextPathname.startsWith("/v3/credin") ||
        nextPathname === "/requestCredin" ||
        nextPathname.startsWith("/requestCredin/") ||
        nextPathname === "/confirmCredin/transaction"
    );
}

/**
 * Clears the v3 shell header (title, back, scopes, right slot). Call on unmount when the screen was embedded.
 *
 * @param {import("redux").Dispatch} dispatch
 * @param {object} [options]
 * @param {string} [options.nextPathname] si la navegación sigue en `/echeqs/*`, no limpia
 *     (handoff al siguiente screen).
 */
export function clearEmbeddedV3ShellLayoutState(dispatch, options = {}) {
    if (!isUxDashboardImprovementsEnabled()) {
        return;
    }
    const { nextPathname } = options;
    if (shouldHandOffEmbeddedLayoutToNextEcheqsRoute(nextPathname)) {
        return;
    }
    if (shouldHandOffEmbeddedLayoutToNextCredinRoute(nextPathname)) {
        return;
    }
    dispatch(layoutActions.setScopes([]));
    dispatch(layoutActions.setTitleKey(""));
    dispatch(layoutActions.setHeaderTitle(""));
    dispatch(layoutActions.setHeaderSubtitle(""));
    dispatch(layoutActions.setHeaderSubtitleElement(null));
    dispatch(layoutActions.setGoBackPath(null));
    dispatch(layoutActions.setRightContentMobile(null));
}

/**
 * Syncs shell title and back for embedded v3 routes. On mobile (or when `syncDesktopShellHeader` is true),
 * updates Redux for {@link PrivateLayout}. By default on desktop it clears those fields so pages can keep
 * using a legacy `Head` without duplicating the shell title.
 *
 * The mobile-only `rightContentMobile` slot is cleared on desktop.
 *
 * @param {import("redux").Dispatch} dispatch
 * @param {object} options
 * @param {boolean} options.embeddedInV3Shell
 * @param {boolean} options.isDesktop
 * @param {string[]} [options.scopes]
 * @param {string} [options.titleKey] i18n key for the title (ignored if `headerTitle` is set)
 * @param {string} [options.headerTitle] literal title (takes precedence over `titleKey`)
 * @param {import("react").ReactNode|null} [options.headerSubtitleElement] custom subtitle content
 * @param {string|null} [options.goBackPath] shell back-button route; default `/v3/dashboard`
 * @param {import("react").ReactNode|null} [options.rightContentMobile] right slot of the mobile header (e.g. menu)
 * @param {boolean} [options.skipRightContentMobileSync] when true, does not dispatch `setRightContentMobile`
 *     (parent owns the slot; avoids `TransactionTicket` overwriting on mount).
 * @param {boolean} [options.syncDesktopShellHeader] when true, sets title/back/scopes on desktop too (shell reads
 *     them in {@link PrivateLayout}); default false keeps legacy clear-on-desktop for pages that render their own
 *     `Head` on desktop
 */
export function applyEmbeddedV3ShellLayoutState(dispatch, options) {
    const {
        embeddedInV3Shell,
        isDesktop,
        syncDesktopShellHeader = false,
        scopes = [],
        titleKey,
        headerTitle,
        headerSubtitleElement,
        goBackPath = "/v3/dashboard",
        rightContentMobile,
        skipRightContentMobileSync = false,
    } = options;

    if (!embeddedInV3Shell || !isUxDashboardImprovementsEnabled()) {
        return;
    }

    dispatch(layoutActions.setScopes(scopes));

    if (isDesktop && !syncDesktopShellHeader) {
        dispatch(layoutActions.setTitleKey(""));
        dispatch(layoutActions.setHeaderTitle(""));
        dispatch(layoutActions.setHeaderSubtitle(""));
        dispatch(layoutActions.setHeaderSubtitleElement(null));
        dispatch(layoutActions.setRightContentMobile(null));
        return;
    }

    dispatch(layoutActions.setHeaderSubtitle(""));

    if (headerTitle != null && headerTitle !== "") {
        dispatch(layoutActions.setTitleKey(""));
        dispatch(layoutActions.setHeaderTitle(headerTitle));
    } else if (titleKey != null && titleKey !== "") {
        dispatch(layoutActions.setHeaderTitle(""));
        dispatch(layoutActions.setTitleKey(titleKey));
    } else {
        dispatch(layoutActions.setTitleKey(""));
        dispatch(layoutActions.setHeaderTitle(""));
    }

    dispatch(layoutActions.setHeaderSubtitleElement(headerSubtitleElement ?? null));
    dispatch(layoutActions.setGoBackPath(goBackPath));

    if (isDesktop) {
        dispatch(layoutActions.setRightContentMobile(null));
    } else if (!skipRightContentMobileSync) {
        if (rightContentMobile !== undefined) {
            dispatch(layoutActions.setRightContentMobile(rightContentMobile));
        } else {
            dispatch(layoutActions.setRightContentMobile(null));
        }
    }
}

/**
 * Hook para pantallas funcionales embebidas en el shell v3: sincroniza header y limpia al desmontar.
 *
 * @param {import("redux").Dispatch} dispatch
 * @param {object} options Ver {@link applyEmbeddedV3ShellLayoutState}
 */
export function useEmbeddedV3ShellLayoutSync(dispatch, options) {
    const {
        embeddedInV3Shell,
        isDesktop,
        syncDesktopShellHeader = false,
        titleKey,
        headerTitle,
        headerSubtitleElement,
        goBackPath = "/v3/dashboard",
        scopes,
        rightContentMobile,
        skipRightContentMobileSync = false,
    } = options;

    const scopesKey = Array.isArray(scopes) ? scopes.join(",") : "";

    useEffect(() => {
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell,
            isDesktop,
            syncDesktopShellHeader,
            titleKey,
            headerTitle,
            headerSubtitleElement,
            goBackPath,
            scopes,
            rightContentMobile,
            skipRightContentMobileSync,
        });
        return () => {
            if (embeddedInV3Shell) {
                const nextPathname =
                    typeof window !== "undefined" && window.location && window.location.pathname
                        ? window.location.pathname
                        : "";
                clearEmbeddedV3ShellLayoutState(dispatch, { nextPathname });
            }
        };
    }, [
        dispatch,
        embeddedInV3Shell,
        isDesktop,
        syncDesktopShellHeader,
        titleKey,
        headerTitle,
        headerSubtitleElement,
        goBackPath,
        scopesKey,
        rightContentMobile,
        skipRightContentMobileSync,
    ]);
}
